import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateOperationRequest,
  operationsService,
  UpdateOperationRequest,
} from "../../../api/services/operations-service";

export const operationsKeys = {
  all: ["operations"] as const,
  lists: () => [...operationsKeys.all, "list"] as const,
  list: () => [...operationsKeys.lists()] as const,
  details: () => [...operationsKeys.all, "detail"] as const,
  detail: (ops: string) => [...operationsKeys.details(), ops] as const,
};

export const useOperations = () => {
  return useQuery({
    queryKey: operationsKeys.list(),
    queryFn: async () => {
      const response = await operationsService.listOperations();
      return response.data?.operations || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useOperation = (ops: string) => {
  return useQuery({
    queryKey: operationsKeys.detail(ops),
    queryFn: async () => {
      const response = await operationsService.getOperation(ops);
      return response.data;
    },
    enabled: !!ops,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useCreateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOperationRequest) =>
      operationsService.createOperation(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operationsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: operationsKeys.detail(variables.ops),
      });
    },
  });
};

export const useUpdateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOperationRequest) =>
      operationsService.updateOperation(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operationsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: operationsKeys.detail(variables.ops),
      });
    },
  });
};

export const useDeleteOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ops: string) => operationsService.deleteOperation(ops),
    onSuccess: (_, ops) => {
      queryClient.invalidateQueries({ queryKey: operationsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: operationsKeys.detail(ops),
      });
    },
  });
};
